import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router";
import { ArrowDown, ArrowUp, ChevronRight, ChevronsDownUp, ChevronsUpDown, Copy, ExternalLink, ListOrdered, Pencil, Plus, Puzzle, Search, Sparkles, Trash2, Undo2, Upload } from "lucide-react";
import { api, ApiError, can, type Coverage, type CurriculumMeta, type CurriculumOptions, type CurriculumTree } from "../lib/api";
import { useStudio } from "../lib/context";
import { Badge, Drawer, Empty, ErrorBox, errorMessage, Field, Loading, PageHead, ProgressBar, Seg } from "../components/ui";
import { progressOf } from "./Dashboard";
import { CurriculumAiDrawer } from "../components/CurriculumAi";

const slugify = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
type Filter = "all" | "todo" | "draft" | "review" | "ok";
const MATCH: Record<Filter, (s: Coverage) => boolean> = {
  all: () => true,
  todo: (s) => s === "MISSING",
  draft: (s) => s === "DRAFT" || s === "AI_DRAFT" || s === "REJECTED",
  review: (s) => s === "IN_REVIEW",
  ok: (s) => s === "VALIDATED",
};
const FOLD_KEY = "jange.studio.folded";

type Cv = CurriculumTree[number];
type TermOption = { ordinal: number; label: string };
const STATUS = { DRAFT: { label: "Brouillon", cls: "b-DRAFT" }, PUBLISHED: { label: "Publié", cls: "b-VALIDATED" }, ARCHIVED: { label: "Archivé", cls: "b-MISSING" } } as const;
const cvLabel = (c: { subject: string; grade: string; track: string | null }) => `${c.subject} · ${c.grade}${c.track ? ` ${c.track}` : ""}`;
const fmtDay = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
const fmtNum = (n: number) => n.toLocaleString("fr-FR");
/** Périodes d'un programme : celles de l'année scolaire, sinon trois périodes numérotées. */
const termsOf = (opts: CurriculumOptions | undefined, yearId: string): TermOption[] => {
  const t = opts?.schoolYears.find((y) => y.id === yearId)?.terms ?? [];
  return t.length ? t : [1, 2, 3].map((ordinal) => ({ ordinal, label: `Période ${ordinal}` }));
};

export function Curriculum() {
  const { countryId, languages, teacher, toast } = useStudio();
  const qc = useQueryClient();
  // Liste légère pour le sélecteur, puis l'arbre du seul programme affiché (rechargé après chaque modification).
  const list = useQuery({ queryKey: ["curricula", countryId, "summary"], queryFn: () => api.curriculumSummary(countryId) });
  const opts = useQuery({ queryKey: ["curriculum-options", countryId], queryFn: () => api.curriculumOptions(countryId), staleTime: 60_000 });
  const isEditor = can.editor(teacher);
  const [cvId, setCvId] = useState<string | null>(null);
  const [panel, setPanel] = useState<null | "new" | "edit" | "copy" | "ai">(null);
  const [organize, setOrganize] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [lang, setLang] = useState(languages[0]?.code ?? "fr");
  const [folded, setFolded] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem(FOLD_KEY) ?? "[]") as string[]); } catch { return new Set(); } });
  const loc = useLocation();
  const selected = list.data?.find((c) => c.id === cvId) ?? list.data?.[0];
  const q = useQuery({ queryKey: ["curricula", countryId, "one", selected?.id], queryFn: () => api.curriculum(countryId, selected!.id), enabled: !!selected });
  const cv = q.data && q.data.id === selected?.id ? q.data : undefined;
  const f = search.trim().toLowerCase();
  const shown = languages.slice(0, 3);
  const terms = cv ? termsOf(opts.data, cv.schoolYearId) : [];
  const termLabel = (n: number | null) => (n === null ? null : terms.find((t) => t.ordinal === n)?.label ?? `Période ${n}`);
  const select = (id: string) => { setCvId(id); setOrganize(false); };
  const refresh = () => qc.invalidateQueries({ queryKey: ["curricula", countryId] });

  const fold = (next: Set<string>) => { setFolded(next); try { localStorage.setItem(FOLD_KEY, JSON.stringify([...next])); } catch { /* rien */ } };
  const all = useMemo(() => cv?.chapters.flatMap((c) => c.notions) ?? [], [cv]);
  const overall = progressOf(all.map((n) => n.coverage[lang] ?? "MISSING"));
  const count = (k: Filter) => all.filter((n) => MATCH[k](n.coverage[lang] ?? "MISSING")).length;
  const filtering = !!f || filter !== "all";
  const editing = isEditor && !!cv && organize;
  // Programme encore vide : l'éditeur de chapitres s'ouvre et reste ouvert après le premier ajout.
  useEffect(() => { if (isEditor && cv && cv.chapters.length === 0) setOrganize(true); }, [isEditor, cv?.id, cv?.chapters.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = useMutation({
    mutationFn: (s: "DRAFT" | "PUBLISHED") => api.updateCurriculum(cv!.id, { status: s }),
    onSuccess: async (r) => { toast(r.status === "PUBLISHED" ? "Programme publié" : "Programme retiré de l'app"); await refresh(); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const publish = () => {
    if (!cv) return;
    const year = opts.data?.schoolYears.find((y) => y.id === cv.schoolYearId);
    const upcoming = !!year && year.startsOn > new Date().toISOString().slice(0, 10);
    // Même règle que l'API : l'année en cours d'abord ; une année à venir sert tout de suite s'il n'y a rien pour l'année en cours.
    const when = upcoming
      ? `Il sera servi aux élèves de ${cv.grade} à partir de la rentrée du ${fmtDay(year.startsOn)}, ou dès maintenant s'il n'existe aucun programme publié pour cette classe et cette matière.`
      : `Les élèves de ${cv.grade} le verront dans l'app.`;
    if (confirm(`Publier ${cvLabel(cv)} (${cv.schoolYear}) ?\n\n${when}`)) status.mutate("PUBLISHED");
  };
  const unpublish = () => { if (cv && confirm(`Retirer ${cvLabel(cv)} (${cv.schoolYear}) de l'app ?\n\nLes élèves ne verront plus ce programme. Leurs progrès sur les notions sont conservés.`)) status.mutate("DRAFT"); };

  // Lien depuis le tableau de bord (#ch-6) : chapitre déplié et amené à l'écran.
  useEffect(() => {
    const m = /^#ch-(\d+)$/.exec(loc.hash);
    if (!m || !cv) return;
    const ch = cv.chapters.find((c) => c.ordinal === Number(m[1]));
    if (!ch) return;
    if (folded.has(ch.id)) { const n = new Set(folded); n.delete(ch.id); fold(n); }
    requestAnimationFrame(() => document.getElementById(`ch-${ch.ordinal}`)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.hash, cv]);

  const meta = cv ? [cv.authority, cv.weeklyHours !== null && `${fmtNum(cv.weeklyHours)} h par semaine`, cv.coefficient !== null && `coefficient ${fmtNum(cv.coefficient)}`, cv.issuedOn && `texte du ${fmtDay(cv.issuedOn)}`].filter(Boolean).join(" · ") : "";

  return (
    <>
      <PageHead kicker="Programme officiel" title={cv ? cvLabel(cv) : "Programme"}
        lead={cv ? `Année ${cv.schoolYear} · ${cv.chapters.length} chapitre${cv.chapters.length > 1 ? "s" : ""} · ${all.length} notion${all.length > 1 ? "s" : ""}` : undefined}>
        <div className="row wrap" style={{ gap: 8 }}>
          {list.data && list.data.length > 1 && (
            <select className="select" style={{ width: 380, maxWidth: "100%" }} value={selected?.id} onChange={(e) => select(e.target.value)} aria-label="Programme">
              {list.data.map((c) => <option key={c.id} value={c.id}>{c.schoolYear} · {cvLabel(c)}{c.status !== "PUBLISHED" ? ` (${STATUS[c.status].label.toLowerCase()})` : ""}</option>)}
            </select>
          )}
          {isEditor && <button className="btn" onClick={() => setPanel("new")}><Plus size={15} />Nouveau programme</button>}
        </div>
      </PageHead>
      {(list.isLoading || (!!selected && q.isLoading)) && <Loading lines={8} />}
      {list.error && <ErrorBox error={list.error} onRetry={() => list.refetch()} />}
      {q.error && <ErrorBox error={q.error} onRetry={() => q.refetch()} />}
      {list.data?.length === 0 && (
        <div className="card"><Empty title="Aucun programme pour ce pays.">
          {isEditor ? <button className="btn sm" style={{ marginTop: 8 }} onClick={() => setPanel("new")}><Plus size={15} />Créer le premier programme</button> : "Un éditeur crée les programmes, par année scolaire, classe et matière."}
        </Empty></div>
      )}
      {cv && (
        <>
          <div className="card appear row wrap" style={{ marginBottom: 16, gap: 10 }}>
            <span className={`badge ${STATUS[cv.status].cls}`}>{STATUS[cv.status].label}</span>
            <span className="small muted">{meta || "Fiche du programme à compléter (texte officiel, horaire, coefficient)."}</span>
            {cv.sourceUrl && <a className="small row" style={{ gap: 4 }} href={cv.sourceUrl} target="_blank" rel="noreferrer noopener"><ExternalLink size={13} />Texte officiel</a>}
            <span className="spacer" />
            {isEditor && (
              <div className="row wrap" style={{ gap: 6 }}>
                <button className="btn sm ghost" onClick={() => setPanel("edit")}><Pencil size={14} />Fiche</button>
                <button className="btn sm ghost" onClick={() => setPanel("copy")}><Copy size={14} />Copier vers une autre année</button>
                <button className="btn sm" onClick={() => setPanel("ai")}><Sparkles size={14} />Créer avec l'IA</button>
                {cv.status === "PUBLISHED"
                  ? <button className="btn sm ghost" disabled={status.isPending} onClick={unpublish}><Undo2 size={14} />Retirer de l'app</button>
                  : <button className="btn sm" disabled={status.isPending || cv.chapters.length === 0} title={cv.chapters.length === 0 ? "Ajoutez d'abord les chapitres" : undefined} onClick={publish}><Upload size={14} />Publier</button>}
              </div>
            )}
          </div>

          {editing ? <ChapterEditor cv={cv} terms={terms} onDone={() => setOrganize(false)} onAi={isEditor ? () => setPanel("ai") : undefined} /> : cv.chapters.length === 0 ? (
            <div className="card"><Empty title="Aucun chapitre pour l'instant.">Un éditeur ajoute les chapitres du programme officiel, ou les fait proposer par l'IA à partir du texte officiel.</Empty></div>
          ) : (
            <>
              <div className="card appear" style={{ marginBottom: 16 }}>
                <div className="row wrap" style={{ marginBottom: 12 }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div className="row" style={{ gap: 8 }}>
                      <span style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: 22 }}>{overall.ok}<span className="muted" style={{ fontSize: 14 }}> / {overall.total}</span></span>
                      <span className="dim strong">leçons validées</span>
                    </div>
                  </div>
                  {languages.length > 1 && <Seg label="Langue suivie" value={lang} onChange={setLang} options={languages.map((l) => ({ value: l.code, label: l.nativeName ?? l.name }))} />}
                </div>
                <ProgressBar p={overall} large />
                <div className="legend" style={{ marginTop: 10 }}>
                  <span><i className="p-ok" />{overall.ok} validées</span><span><i className="p-review" />{overall.review} en relecture</span>
                  <span><i className="p-draft" />{overall.draft} brouillons</span><span><i style={{ background: "var(--surface-3)", border: "1px dashed var(--border-strong)" }} />{overall.total - overall.ok - overall.review - overall.draft} à écrire</span>
                </div>
              </div>

              <div className="toolbar">
                <div className="input-icon" style={{ width: 280 }}>
                  <Search size={15} />
                  <input className="input" placeholder="Filtrer les notions…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Filtrer les notions" />
                </div>
                <Seg label="Statut" value={filter} onChange={setFilter} options={[
                  { value: "all", label: "Toutes", count: all.length }, { value: "todo", label: "À écrire", count: count("todo") },
                  { value: "draft", label: "Brouillons", count: count("draft") }, { value: "review", label: "En relecture", count: count("review") }, { value: "ok", label: "Validées", count: count("ok") },
                ]} />
                <span className="spacer" />
                {isEditor && <button className="btn sm subtle" onClick={() => setOrganize(true)}><ListOrdered size={15} />Organiser les chapitres</button>}
                {!filtering && (folded.size > 0
                  ? <button className="btn sm subtle" onClick={() => fold(new Set())}><ChevronsUpDown size={15} />Tout déplier</button>
                  : <button className="btn sm subtle" onClick={() => fold(new Set(cv.chapters.map((c) => c.id)))}><ChevronsDownUp size={15} />Tout replier</button>)}
              </div>

              <div className="chapters">
                {cv.chapters.map((ch) => {
                  const notions = ch.notions.filter((n) => (!f || n.title.toLowerCase().includes(f)) && MATCH[filter](n.coverage[lang] ?? "MISSING"));
                  if (filtering && notions.length === 0) return null;
                  const open = filtering || !folded.has(ch.id);
                  const p = progressOf(ch.notions.map((n) => n.coverage[lang] ?? "MISSING"));
                  const term = termLabel(ch.termOrdinal);
                  return (
                    <section key={ch.id} id={`ch-${ch.ordinal}`} className="chapter appear" style={{ scrollMarginTop: 80 }}>
                      <button type="button" className="chapter-head" aria-expanded={open} aria-controls={`chb-${ch.id}`}
                        onClick={() => { if (filtering) return; const n = new Set(folded); if (n.has(ch.id)) n.delete(ch.id); else n.add(ch.id); fold(n); }}>
                        <span className="num">{ch.ordinal}</span>
                        <div style={{ minWidth: 0 }}>
                          <div className="tiny muted strong">Chapitre {ch.ordinal}{term ? ` · ${term}` : ""}</div>
                          <h3>{ch.title}</h3>
                        </div>
                        <div className="prog stack" style={{ gap: 6 }}>
                          <span className="tiny muted strong" style={{ textAlign: "right" }}>{p.ok}/{p.total} validées</span>
                          <ProgressBar p={p} />
                        </div>
                        <ChevronRight className="chev" size={18} />
                      </button>
                      {open && (
                        <div className="chapter-body" id={`chb-${ch.id}`} style={{ ["--langs" as string]: shown.length }}>
                          {notions.length > 0 && (
                            <div className="notion-row head" aria-hidden>
                              <span>Notion</span>{shown.map((l, i) => <span key={l.code} className={i ? "hide-sm" : ""}>{l.nativeName ?? l.name}</span>)}<span className="hide-sm">Exercices</span><span />
                            </div>
                          )}
                          {notions.map((n) => (
                            <Link key={n.id} to={`/notions/${n.id}`} className="notion-row">
                              <span className="title">{n.title}</span>
                              {shown.map((l, i) => <span key={l.code} className={i ? "hide-sm" : ""}><Badge status={n.coverage[l.code] ?? "MISSING"} /></span>)}
                              <span className="hide-sm small muted row" style={{ gap: 5 }}><Puzzle size={14} />{n.exercises} gabarit{n.exercises > 1 ? "s" : ""}</span>
                              <ChevronRight className="go" size={16} />
                            </Link>
                          ))}
                          {notions.length === 0 && <div className="muted small" style={{ padding: "8px 10px" }}>Aucune notion dans ce chapitre.</div>}
                          {can.author(teacher) && !filtering && <NewNotion chapterId={ch.id} />}
                        </div>
                      )}
                    </section>
                  );
                })}
                {filtering && !cv.chapters.some((ch) => ch.notions.some((n) => (!f || n.title.toLowerCase().includes(f)) && MATCH[filter](n.coverage[lang] ?? "MISSING"))) && (
                  <div className="card"><Empty icon={<Search size={20} />} title="Aucune notion ne correspond.">Modifiez la recherche ou le filtre de statut.</Empty></div>
                )}
              </div>
            </>
          )}
        </>
      )}
      {panel === "new" && <NewCurriculum options={opts.data} onClose={() => setPanel(null)} onCreated={(id) => { setPanel(null); select(id); }} />}
      {panel === "edit" && cv && <EditCurriculum cv={cv} onClose={() => setPanel(null)} />}
      {panel === "copy" && cv && <CopyCurriculum cv={cv} options={opts.data} onClose={() => setPanel(null)} onCreated={(id) => { setPanel(null); select(id); }} />}
      {panel === "ai" && cv && <CurriculumAiDrawer cvId={cv.id} cvLabel={`${cvLabel(cv)} · ${cv.schoolYear}`} terms={terms} onClose={() => setPanel(null)} onApplied={() => { setPanel(null); setOrganize(false); }} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Fiche du programme : création, modification, copie vers une autre année
// ---------------------------------------------------------------------------
type MetaForm = { authority: string; issuedOn: string; sourceUrl: string; weeklyHours: string; coefficient: string };
const toForm = (m?: CurriculumMeta): MetaForm => ({
  authority: m?.authority ?? "", issuedOn: m?.issuedOn ?? "", sourceUrl: m?.sourceUrl ?? "",
  weeklyHours: m?.weeklyHours != null ? String(m.weeklyHours) : "", coefficient: m?.coefficient != null ? String(m.coefficient) : "",
});
const num = (s: string) => (s.trim() ? Number(s.replace(",", ".")) : null);
const fromForm = (f: MetaForm): CurriculumMeta => ({
  authority: f.authority.trim() || null, issuedOn: f.issuedOn || null, sourceUrl: f.sourceUrl.trim() || null, weeklyHours: num(f.weeklyHours), coefficient: num(f.coefficient),
});

function MetaFields({ value, onChange }: { value: MetaForm; onChange: (v: MetaForm) => void }) {
  const set = (k: keyof MetaForm) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <>
      <Field label="Autorité" hint="Ex. MEN / IGEF"><input className="input" value={value.authority} onChange={set("authority")} maxLength={120} /></Field>
      <div className="row" style={{ gap: 10 }}>
        <Field label="Heures par semaine"><input className="input" style={{ width: 150 }} type="number" min={0.5} max={40} step={0.5} value={value.weeklyHours} onChange={set("weeklyHours")} /></Field>
        <Field label="Coefficient"><input className="input" style={{ width: 150 }} type="number" min={0.5} max={20} step={0.5} value={value.coefficient} onChange={set("coefficient")} /></Field>
      </div>
      <Field label="Date du texte officiel"><input className="input" type="date" value={value.issuedOn} onChange={set("issuedOn")} /></Field>
      <Field label="Lien vers le texte officiel (PDF)"><input className="input" type="url" placeholder="https://" value={value.sourceUrl} onChange={set("sourceUrl")} maxLength={500} /></Field>
    </>
  );
}

/** Programme déjà existant (409) : on l'ouvre plutôt que d'afficher une erreur. */
const existingId = (e: unknown) => (e instanceof ApiError && e.code === "curriculum_exists" ? (e.detail as { id?: string | null } | undefined)?.id ?? null : null);

function NewCurriculum({ options, onClose, onCreated }: { options: CurriculumOptions | undefined; onClose: () => void; onCreated: (id: string) => void }) {
  const { countryId, toast } = useStudio();
  const qc = useQueryClient();
  const [schoolYearId, setYear] = useState(options?.schoolYears[0]?.id ?? "");
  // Options arrivées après l'ouverture du panneau : l'année affichée est aussi celle envoyée.
  useEffect(() => { if (!schoolYearId && options?.schoolYears[0]) setYear(options.schoolYears[0].id); }, [options, schoolYearId]);
  const [gradeId, setGrade] = useState("");
  const [trackId, setTrack] = useState("");
  const [subjectId, setSubject] = useState("");
  const [meta, setMeta] = useState<MetaForm>(toForm());
  const cycles = [...new Set(options?.grades.map((g) => g.cycle) ?? [])];
  const create = useMutation({
    mutationFn: () => api.createCurriculum({ countryId, schoolYearId, gradeId, trackId: trackId || null, subjectId, ...fromForm(meta) }),
    onSuccess: (r) => { toast("Programme créé en brouillon : ajoutez ses chapitres"); void qc.invalidateQueries({ queryKey: ["curricula", countryId] }).then(() => onCreated(r.id)); },
    onError: (e) => {
      const id = existingId(e);
      if (id) { toast("Ce programme existe déjà : il est ouvert", "err"); onCreated(id); return; }
      toast(errorMessage(e), "err");
    },
  });
  const ready = !!schoolYearId && !!gradeId && !!subjectId;
  const submit = (e: FormEvent) => { e.preventDefault(); if (ready && !create.isPending) create.mutate(); };
  return (
    <Drawer title="Nouveau programme" subtitle="Un programme par année scolaire, classe, série et matière. Il reste en brouillon jusqu'à sa publication." onClose={onClose}>
      {!options ? <Loading /> : (
        <form className="stack" style={{ gap: 12 }} onSubmit={submit}>
          <Field label="Année scolaire">
            <select className="select" value={schoolYearId} onChange={(e) => setYear(e.target.value)}>
              {options.schoolYears.map((y) => <option key={y.id} value={y.id}>{y.label}</option>)}
            </select>
          </Field>
          <Field label="Classe">
            <select className="select" value={gradeId} onChange={(e) => setGrade(e.target.value)}>
              <option value="">Choisir…</option>
              {cycles.map((c) => <optgroup key={c} label={c}>{options.grades.filter((g) => g.cycle === c).map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</optgroup>)}
            </select>
          </Field>
          <Field label="Série" hint="Sans série, le programme vaut pour toutes les séries de la classe.">
            <select className="select" value={trackId} onChange={(e) => setTrack(e.target.value)}>
              <option value="">Aucune (toutes les séries)</option>
              {options.tracks.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.label}</option>)}
            </select>
          </Field>
          <Field label="Matière" hint="Une matière manque ? L'administrateur l'ajoute dans Référentiel.">
            <select className="select" value={subjectId} onChange={(e) => setSubject(e.target.value)}>
              <option value="">Choisir…</option>
              {options.subjects.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          <MetaFields value={meta} onChange={setMeta} />
          <div className="row" style={{ gap: 8 }}>
            <button className="btn" disabled={!ready || create.isPending}>Créer le programme</button>
            <button type="button" className="btn ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>
      )}
    </Drawer>
  );
}

function EditCurriculum({ cv, onClose }: { cv: Cv; onClose: () => void }) {
  const { countryId, toast } = useStudio();
  const qc = useQueryClient();
  const [meta, setMeta] = useState<MetaForm>(toForm(cv));
  const save = useMutation({
    mutationFn: () => api.updateCurriculum(cv.id, fromForm(meta)),
    onSuccess: () => { toast("Fiche enregistrée"); void qc.invalidateQueries({ queryKey: ["curricula", countryId] }); onClose(); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  return (
    <Drawer title="Fiche du programme" subtitle={`${cvLabel(cv)} · ${cv.schoolYear}`} onClose={onClose}>
      <form className="stack" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); if (!save.isPending) save.mutate(); }}>
        <MetaFields value={meta} onChange={setMeta} />
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" disabled={save.isPending}>Enregistrer</button>
          <button type="button" className="btn ghost" onClick={onClose}>Annuler</button>
        </div>
      </form>
    </Drawer>
  );
}

function CopyCurriculum({ cv, options, onClose, onCreated }: { cv: Cv; options: CurriculumOptions | undefined; onClose: () => void; onCreated: (id: string) => void }) {
  const { countryId, toast } = useStudio();
  const qc = useQueryClient();
  const years = options?.schoolYears.filter((y) => y.id !== cv.schoolYearId) ?? [];
  const [schoolYearId, setYear] = useState(years[0]?.id ?? "");
  useEffect(() => { if (!schoolYearId && years[0]) setYear(years[0].id); }, [years, schoolYearId]);
  const copy = useMutation({
    mutationFn: () => api.duplicateCurriculum(cv.id, schoolYearId),
    onSuccess: (r) => { toast(`Copie créée en brouillon : ${r.chapters} chapitre${r.chapters > 1 ? "s" : ""}`); void qc.invalidateQueries({ queryKey: ["curricula", countryId] }).then(() => onCreated(r.id)); },
    onError: (e) => {
      const id = existingId(e);
      if (id) { toast("Ce programme existe déjà pour cette année : il est ouvert", "err"); onCreated(id); return; }
      toast(errorMessage(e), "err");
    },
  });
  return (
    <Drawer title="Copier vers une autre année" subtitle={`${cvLabel(cv)} · ${cv.schoolYear}`} onClose={onClose}>
      {!options ? <Loading /> : years.length === 0 ? (
        <Empty title="Aucune autre année scolaire.">L'administrateur crée l'année suivante dans Référentiel.</Empty>
      ) : (
        <form className="stack" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); if (schoolYearId && !copy.isPending) copy.mutate(); }}>
          <p className="small muted" style={{ margin: 0 }}>La copie reprend la fiche, les chapitres et leurs notions, avec les mêmes leçons. Elle reste en brouillon : ajustez-la, puis publiez-la.</p>
          <Field label="Année scolaire">
            <select className="select" value={schoolYearId} onChange={(e) => setYear(e.target.value)}>
              {years.map((y) => <option key={y.id} value={y.id}>{y.label}</option>)}
            </select>
          </Field>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn" disabled={!schoolYearId || copy.isPending}>Copier</button>
            <button type="button" className="btn ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Chapitres : ajout, renommage, période, ordre, suppression d'un chapitre vide
// ---------------------------------------------------------------------------
function ChapterEditor({ cv, terms, onDone, onAi }: { cv: Cv; terms: TermOption[]; onDone: () => void; onAi?: () => void }) {
  const { countryId, toast } = useStudio();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["curricula", countryId] });
  const fail = (e: unknown) => { toast(errorMessage(e), "err"); if (e instanceof ApiError && e.code === "chapters_changed") void refresh(); };
  // Chaque modification reste « en cours » jusqu'au rechargement : un second clic part toujours de l'ordre à jour.
  const save = useMutation({ mutationFn: ({ id, b }: { id: string; b: { title?: string; termOrdinal?: number | null } }) => api.updateChapter(id, b), onSuccess: () => refresh(), onError: fail });
  const move = useMutation({ mutationFn: (ids: string[]) => api.orderChapters(cv.id, ids), onSuccess: () => refresh(), onError: fail });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteChapter(id), onSuccess: () => { toast("Chapitre supprimé"); return refresh(); }, onError: fail });
  const busy = move.isPending || remove.isPending;
  const swap = (i: number, j: number) => {
    const ids = cv.chapters.map((c) => c.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    move.mutate(ids);
  };

  return (
    <div className="card stack appear" style={{ gap: 10 }}>
      <div className="row">
        <div className="stack" style={{ gap: 2, flex: 1 }}>
          <h3 style={{ fontSize: 16 }}>Chapitres du programme</h3>
          <span className="small muted">Dans l'ordre du programme officiel. Un chapitre qui contient des notions ne peut pas être supprimé.</span>
        </div>
        {onAi && <button className="btn sm ghost" onClick={onAi}><Sparkles size={14} />Créer avec l'IA</button>}
        {cv.chapters.length > 0 && <button className="btn sm" onClick={onDone}>Terminé</button>}
      </div>
      {cv.chapters.map((ch, i) => (
        <ChapterRow key={ch.id} ch={ch} terms={terms} first={i === 0} last={i === cv.chapters.length - 1} busy={busy}
          onSave={(b) => save.mutate({ id: ch.id, b })} onUp={() => swap(i, i - 1)} onDown={() => swap(i, i + 1)}
          onDelete={() => { if (confirm(`Supprimer le chapitre « ${ch.title} » ?`)) remove.mutate(ch.id); }} />
      ))}
      <NewChapter key={cv.id} cvId={cv.id} terms={terms} first={cv.chapters.length === 0} />
    </div>
  );
}

function ChapterRow({ ch, terms, first, last, busy, onSave, onUp, onDown, onDelete }: {
  ch: Cv["chapters"][number]; terms: TermOption[]; first: boolean; last: boolean; busy: boolean;
  onSave: (b: { title?: string; termOrdinal?: number | null }) => void; onUp: () => void; onDown: () => void; onDelete: () => void;
}) {
  const [title, setTitle] = useState(ch.title);
  useEffect(() => setTitle(ch.title), [ch.title]);
  const commit = () => { const t = title.trim(); if (t.length >= 2 && t !== ch.title) onSave({ title: t }); else setTitle(ch.title); };
  const full = ch.notions.length > 0;
  return (
    <div className="row wrap" style={{ gap: 8, padding: "6px 0", borderTop: "1px solid var(--border)" }}>
      <span className="mono muted" style={{ width: 24, textAlign: "right" }}>{ch.ordinal}</span>
      <input className="input" style={{ flex: "1 1 260px" }} value={title} maxLength={160} aria-label={`Titre du chapitre ${ch.ordinal}`}
        onChange={(e) => setTitle(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setTitle(ch.title); }} />
      <select className="select" style={{ width: 160 }} value={ch.termOrdinal ?? ""} aria-label={`Période du chapitre ${ch.ordinal}`}
        onChange={(e) => onSave({ termOrdinal: e.target.value ? Number(e.target.value) : null })}>
        <option value="">Période : —</option>
        {terms.map((t) => <option key={t.ordinal} value={t.ordinal}>{t.label}</option>)}
      </select>
      <span className="small muted" style={{ width: 80 }}>{ch.notions.length} notion{ch.notions.length > 1 ? "s" : ""}</span>
      <button className="icon-btn bordered" disabled={first || busy} onClick={onUp} aria-label={`Monter le chapitre ${ch.ordinal}`}><ArrowUp size={15} /></button>
      <button className="icon-btn bordered" disabled={last || busy} onClick={onDown} aria-label={`Descendre le chapitre ${ch.ordinal}`}><ArrowDown size={15} /></button>
      <button className="icon-btn bordered" disabled={full || busy} onClick={onDelete} aria-label={`Supprimer le chapitre ${ch.ordinal}`}
        title={full ? "Ce chapitre contient des notions : il ne peut pas être supprimé" : "Supprimer"}><Trash2 size={15} /></button>
    </div>
  );
}

function NewChapter({ cvId, terms, first }: { cvId: string; terms: TermOption[]; first: boolean }) {
  const { countryId, toast } = useStudio();
  const qc = useQueryClient();
  // Identifiant créé ici : un renvoi (réseau instable) ne crée pas de second chapitre.
  const [id, setId] = useState(() => crypto.randomUUID());
  const [title, setTitle] = useState("");
  const [term, setTerm] = useState("");
  const add = useMutation({
    mutationFn: () => api.addChapter(cvId, { id, title: title.trim(), termOrdinal: term ? Number(term) : null }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["curricula", countryId] }); setId(crypto.randomUUID()); setTitle(""); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); if (!add.isPending && title.trim().length >= 2) add.mutate(); };
  return (
    <form className="row wrap" style={{ gap: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }} onSubmit={submit}>
      <span style={{ width: 24 }} />
      <input className="input" style={{ flex: "1 1 260px" }} placeholder={first ? "Titre du premier chapitre" : "Titre du chapitre suivant"} aria-label="Titre du nouveau chapitre"
        value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} autoFocus={first} />
      <select className="select" style={{ width: 160 }} value={term} onChange={(e) => setTerm(e.target.value)} aria-label="Période du nouveau chapitre">
        <option value="">Période : —</option>
        {terms.map((t) => <option key={t.ordinal} value={t.ordinal}>{t.label}</option>)}
      </select>
      <button className="btn sm" disabled={add.isPending || title.trim().length < 2}><Plus size={15} />Ajouter le chapitre</button>
    </form>
  );
}

function NewNotion({ chapterId }: { chapterId: string }) {
  const { countryId, toast } = useStudio();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const m = useMutation({
    mutationFn: () => api.createNotion(chapterId, slugify(title), title.trim()),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["curricula", countryId] }); toast("Notion créée"); nav(`/notions/${r.id}`); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  if (!open) return <button className="btn sm subtle" style={{ margin: "4px 2px" }} onClick={() => setOpen(true)}><Plus size={15} />Ajouter une notion</button>;
  const submit = (e: FormEvent) => { e.preventDefault(); if (!m.isPending && slugify(title).length >= 3) m.mutate(); };
  return (
    <form className="row wrap appear" style={{ padding: "8px 10px" }} onSubmit={submit}>
      <input className="input" style={{ flex: "1 1 260px" }} autoFocus placeholder="Titre de la notion" aria-label="Titre de la notion" value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} />
      <span className="mono muted" style={{ minWidth: 160 }}>{slugify(title) || "identifiant"}</span>
      <button className="btn sm" disabled={m.isPending || slugify(title).length < 3}>Créer</button>
      <button type="button" className="btn sm ghost" onClick={() => setOpen(false)}>Annuler</button>
    </form>
  );
}
